import { Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { LocalWebTicket, RidangoReduxAction, ShoppingCart } from '@ridango/core';

import { ShoppingCartActionsV2 } from './shopping-cart-v2.actions';
import { ShoppingCartActions } from './shopping-cart.actions';
import { ShoppingCartPartialStoreState } from './shopping-cart.reducer';
import * as ShoppingCartSelectors from './shopping-cart.selectors';
import { map } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

@Injectable()
export class ShoppingCartFacade {
    ticketPayloadsPosted$ = this.store.select(ShoppingCartSelectors.getTicketPayloadsPosted);
    cart$ = this.store.select(ShoppingCartSelectors.getShopingCart);
    docNums$ = this.store.select(ShoppingCartSelectors.getDocNums);
    ticketsGrouped$ = this.store.select(ShoppingCartSelectors.getTicketsGrouped);
    termsAgreed$ = this.store.select(ShoppingCartSelectors.getTermsAgreed);
    failedTickets$ = this.store.select(ShoppingCartSelectors.failedTickets);
    groupedTicketsByProduct$ = combineLatest([
        this.store.select(ShoppingCartSelectors.getDocNums),
        this.store.select(ShoppingCartSelectors.getShoppingCartTickets)
    ]).pipe(
        map(([docNums, tickets]) => {
            const groupedTickets = {};
            for (const dn of docNums) {
                const ticket = tickets[dn];
                const productId = ticket.product_id;
                if (!groupedTickets[productId]) {
                    groupedTickets[productId] = {
                        ...ticket,
                        docNums: [ticket.doc_num]
                    };
                    delete groupedTickets[productId].doc_num;
                    delete groupedTickets[productId].visual_card_id;
                    delete groupedTickets[productId].card_id;
                } else {
                    groupedTickets[productId].docNums.push(ticket.doc_num);
                    groupedTickets[productId].price += ticket.price;
                    groupedTickets[productId].base_price += ticket.base_price;
                }
            }
            return Object.values(groupedTickets);
        })
    );

    constructor(
        private store: NgRedux<ShoppingCartPartialStoreState>,
        private actions: ShoppingCartActions,
        private actionsV2: ShoppingCartActionsV2,
    ) {}

    dispatch(action: RidangoReduxAction) {
        this.store.dispatch(action);
    }

    postPayloads(purchase: any) {
        this.dispatch(this.actionsV2.postTicketPayloads(purchase));
    }

    updateCart(shopping_cart_id: number) {
        this.dispatch(this.actionsV2.fetchCartContents(shopping_cart_id));
    }

    deleteCart() {
        this.dispatch(this.actionsV2.deleteCart());
    }

    removeTicketFromCartState(ticket: LocalWebTicket, cart: ShoppingCart) {
        this.dispatch(this.actionsV2.removeTicketFromCart(ticket, cart));
    }

    deleteTicketFromCart(ticket: LocalWebTicket, cart: ShoppingCart) {
        this.dispatch(this.actionsV2.deleteTicketFromCart(ticket, cart));
    }

    setCartAgreement(termsAgreed) {
        this.dispatch(this.actionsV2.setCartTermsAgreed(termsAgreed));
    }

    setCartStatus(status) {
        this.dispatch(this.actionsV2.setStatus(status));
    }

    hydrateState(cart) {
        this.dispatch(this.actionsV2.hydrateCartState(cart));
    }

    updateCartTotalPrice(price) {
        this.dispatch(this.actionsV2.updateCartTotalPrice(price));
    }

    setInWebPaymentSelected(inWebPaymentSelected) {
        this.dispatch(this.actionsV2.setInWebPaymentSelected(inWebPaymentSelected));
    }
}
